import { assign, sendParent, setup } from 'xstate';
import { getDirections, getNextDepartures, getStops, isScheduleAvailable, ROUTES } from '../data/timetable';
import type { RouteId, ScheduledDeparture, BusTimesEvent } from './events';

interface BusTimesContext {
  routeId: RouteId | null;
  directionId: string | null;
  stopId: string | null;
  departures: ScheduledDeparture[];
  checkedAt: string | null;
  error: string | null;
}

const initialContext: BusTimesContext = {
  routeId: null,
  directionId: null,
  stopId: null,
  departures: [],
  checkedAt: null,
  error: null,
};

const busTimesSetup = setup({
  types: {
    context: {} as BusTimesContext,
    events: {} as BusTimesEvent,
    input: {} as Record<string, never>,
  },
  guards: {
    routeExists: ({ event }) =>
      event.type === 'bus-times.choose-route' && ROUTES.some((route: { id: RouteId }) => route.id === event.routeId),
    directionExists: ({ context, event }) =>
      event.type === 'bus-times.choose-direction' &&
      context.routeId !== null &&
      getDirections(context.routeId).some(
        (direction: { id: string; label: string }) => direction.id === event.directionId,
      ),
    stopExists: ({ context, event }) =>
      event.type === 'bus-times.choose-stop' &&
      context.routeId !== null &&
      context.directionId !== null &&
      getStops(context.routeId, context.directionId).some(
        (stop: { id: string; name: string }) => stop.id === event.stopId,
      ),
    hasSchedule: ({ context }) => context.stopId !== null && context.checkedAt !== null,
    scheduleViewAvailable: ({ context }) => context.stopId !== null && isScheduleAvailable(new Date()),
    scheduleViewUnavailable: ({ context }) => context.stopId !== null && !isScheduleAvailable(new Date()),
    refreshAvailable: ({ context }) =>
      context.stopId !== null && context.checkedAt !== null && isScheduleAvailable(new Date()),
  },
  actions: {
    chooseRoute: assign(({ event }) => {
      if (event.type !== 'bus-times.choose-route') return {};
      return {
        routeId: event.routeId,
        directionId: null,
        stopId: null,
        departures: [],
        checkedAt: null,
        error: null,
      };
    }),
    chooseDirection: assign(({ event }) => {
      if (event.type !== 'bus-times.choose-direction') return {};
      return {
        directionId: event.directionId,
        stopId: null,
        departures: [],
        checkedAt: null,
        error: null,
      };
    }),
    chooseStop: assign(({ event }) => {
      if (event.type !== 'bus-times.choose-stop') return {};
      return {
        stopId: event.stopId,
        departures: [],
        checkedAt: null,
        error: null,
      };
    }),
    loadSchedule: assign(({ context }) => {
      if (context.routeId === null || context.directionId === null || context.stopId === null) {
        return {};
      }

      const checkedAt = new Date();
      return {
        departures: getNextDepartures(context.routeId, context.directionId, context.stopId, checkedAt),
        checkedAt: checkedAt.toISOString(),
        error: isScheduleAvailable(checkedAt) ? null : 'The bundled schedule is unavailable for this date.',
      };
    }),
  },
});

export const busTimesMachine = busTimesSetup.createMachine({
  id: 'bus-times',
  initial: 'route',
  context: initialContext,
  states: {
    route: {},
    direction: {},
    stop: {},
    selected: {},
    ready: {},
    unavailable: {},
  },
  on: {
    'bus-times.choose-route': {
      target: '.direction',
      guard: 'routeExists',
      actions: ['chooseRoute', sendParent(({ event }) => ({ type: 'bus-times.route-chosen', routeId: event.routeId }))],
    },
    'bus-times.choose-direction': {
      target: '.stop',
      guard: 'directionExists',
      actions: [
        'chooseDirection',
        sendParent(({ event }) => ({ type: 'bus-times.direction-chosen', directionId: event.directionId })),
      ],
    },
    'bus-times.choose-stop': {
      target: '.selected',
      guard: 'stopExists',
      actions: ['chooseStop', sendParent(({ event }) => ({ type: 'bus-times.stop-chosen', stopId: event.stopId }))],
    },
    'bus-times.view-schedule': [
      {
        target: '.ready',
        guard: 'scheduleViewAvailable',
        actions: [
          'loadSchedule',
          sendParent(({ context }) => ({ type: 'bus-times.schedule-viewed', stopId: context.stopId ?? '' })),
        ],
      },
      {
        target: '.unavailable',
        guard: 'scheduleViewUnavailable',
        actions: [
          'loadSchedule',
          sendParent(({ context }) => ({ type: 'bus-times.schedule-viewed', stopId: context.stopId ?? '' })),
        ],
      },
    ],
    'bus-times.refresh-schedule': [
      {
        target: '.ready',
        guard: 'refreshAvailable',
        actions: [
          'loadSchedule',
          sendParent(({ context }) => ({ type: 'bus-times.schedule-refreshed', stopId: context.stopId ?? '' })),
        ],
      },
      {
        target: '.unavailable',
        guard: 'hasSchedule',
        actions: [
          'loadSchedule',
          sendParent(({ context }) => ({ type: 'bus-times.schedule-refreshed', stopId: context.stopId ?? '' })),
        ],
      },
    ],
  },
});
